/** The Base & Mode tab: what the wizard writes (base transaction, line form,
    add / edit / add-or-edit and how the record is found), its signature
    (inputs / outputs — what every launch path fills and every caller reads
    back), its scratch variables, how it ends and pauses, and the two keys that
    bound what a save through it may change (`scope.edit_when`, `also_writes`,
    design §12.3). */

import { FunctionsEditor } from './ExternalEditor'
import ExprInput from './ExprInput'
import { literalText, typedDefault } from './literals'
import {
  ColumnMulti, KeyValueEditor, LineFormPicker, ObjectPicker, StepPicker, TargetPicker,
} from './pickers'
import { headerForm, lineForms, useWizardDesign } from './context'
import type { WizardInput, WizardModel, WizardVar } from './types'
import {
  AddBtn, Check, Field, Grid2, NumberInput, RemoveBtn, SectionHeader, SelectInput, TextInput,
  cardStyle, emptyHintStyle, hintStyle, inputStyle,
} from './ui'

const TYPES = ['string', 'number', 'date', 'boolean']

function InputsEditor({ model, onChange }: { model: WizardModel; onChange: (m: WizardModel) => void }) {
  const ctx = useWizardDesign()
  const list = model.inputs || []
  const set = (i: number, p: Partial<WizardInput>) => onChange({ ...model, inputs: list.map((x, j) => (j === i ? { ...x, ...p } : x)) })
  return (
    <div>
      {list.length === 0 && <div style={emptyHintStyle}>No inputs — the wizard starts empty.</div>}
      {list.map((inp, i) => (
        <div key={i} style={{ ...cardStyle, padding: 8, marginBottom: 6 }}>
          <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
            <div style={{ flex: 1 }}>
              <Grid2>
                <Field label="Name ({input.NAME}, URL ?NAME=)">
                  <TextInput mono value={inp.name} onChange={(name) => set(i, { name: name.replace(/[^A-Za-z0-9_]/g, '_') })} />
                </Field>
                <Field label="Label">
                  <TextInput value={inp.label} onChange={(label) => set(i, { label: label || undefined })} />
                </Field>
                <Field label="Type">
                  <SelectInput value={inp.type || 'string'} options={TYPES} onChange={(type) => set(i, { type: type as WizardInput['type'] })} />
                </Field>
                <Field label="Default">
                  {/* Typed by the input's type (a number default is 5, not "5"). */}
                  <TextInput value={literalText(inp.default)}
                    onChange={(d) => set(i, { default: typedDefault(d, inp.type) })} />
                </Field>
              </Grid2>
              <Field label="Set into (optional — without it, only readable as {input.NAME})">
                <TargetPicker value={inp.target} onChange={(target) => set(i, { target: target || undefined })} />
              </Field>
              <div style={{ display: 'flex', gap: 18 }}>
                <Check checked={inp.required} onChange={(required) => set(i, { required: required || undefined })}
                  label="Required" hint="Missing at launch → asked for in an automatic first step." />
                <Check checked={inp.lockable} onChange={(lockable) => set(i, { lockable: lockable || undefined })}
                  label="A caller may lock it" hint="Shown read-only when a calling wizard locks it." />
              </div>
            </div>
            {!ctx.readOnly && <RemoveBtn onClick={() => onChange({ ...model, inputs: list.filter((_, j) => j !== i) })} />}
          </div>
        </div>
      ))}
      {!ctx.readOnly && <AddBtn onClick={() => onChange({ ...model, inputs: [...list, { name: `input_${list.length + 1}`, type: 'string' }] })}>Add input</AddBtn>}
    </div>
  )
}

function VarsEditor({ model, onChange }: { model: WizardModel; onChange: (m: WizardModel) => void }) {
  const ctx = useWizardDesign()
  const list = model.vars || []
  const set = (i: number, p: Partial<WizardVar>) => onChange({ ...model, vars: list.map((x, j) => (j === i ? { ...x, ...p } : x)) })
  return (
    <div>
      {list.length === 0 && <div style={emptyHintStyle}>No variables.</div>}
      {list.map((v, i) => (
        <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 4 }}>
          <TextInput mono value={v.name} onChange={(name) => set(i, { name: name.replace(/[^A-Za-z0-9_]/g, '_') })} placeholder="name" />
          <SelectInput value={v.type || 'string'} options={TYPES} onChange={(type) => set(i, { type: type as WizardVar['type'] })} />
          <TextInput value={literalText(v.default)} placeholder="default"
            onChange={(d) => set(i, { default: typedDefault(d, v.type) })} />
          {!ctx.readOnly && <RemoveBtn onClick={() => onChange({ ...model, vars: list.filter((_, j) => j !== i) })} />}
        </div>
      ))}
      {!ctx.readOnly && <AddBtn onClick={() => onChange({ ...model, vars: [...list, { name: `var_${list.length + 1}`, type: 'string' }] })}>Add variable</AddBtn>}
      <div style={hintStyle}>Variables are never saved unless a step sets one onto a column.</div>
    </div>
  )
}

export default function BaseTab({ onChange }: { onChange: (m: WizardModel) => void }) {
  const ctx = useWizardDesign()
  const { model } = ctx
  const patch = (p: Partial<WizardModel>) => {
    const next = { ...model, ...p } as Record<string, unknown>
    for (const k of Object.keys(p)) if (next[k] === undefined) delete next[k]
    onChange(next as unknown as WizardModel)
  }
  const header = headerForm(ctx.base)
  const editing = model.mode === 'edit' || model.mode === 'add_or_edit'
  const finishThen = model.finish?.then || 'restart'
  const finishMode = finishThen.startsWith('goto:') ? 'goto' : finishThen
  const alsoHeader = (model.also_writes || []).filter((x) => x.startsWith('header.'))
  const alsoLine = (model.also_writes || []).filter((x) => !x.startsWith('header.'))
  return (
    <div>
      <div style={cardStyle}>
        <SectionHeader>The wizard</SectionHeader>
        <Grid2>
          <Field label="Title"><TextInput value={model.title} onChange={(title) => patch({ title })} /></Field>
          <Field label="Base transaction (the T this wizard writes)">
            <ObjectPicker objType="T" value={model.base_object} onChange={(base_object) => patch({ base_object })} />
          </Field>
          <Field label="Line form ({line.X} by default)">
            <LineFormPicker value={model.line_form} empty={lineForms(ctx.base).length ? '— none —' : 'No line forms'}
              onChange={(line_form) => patch({ line_form: line_form || undefined })} />
          </Field>
        </Grid2>
        <Field label="Description">
          <textarea value={model.description || ''} onChange={(e) => patch({ description: e.target.value || undefined })}
            style={{ ...inputStyle, minHeight: 44, resize: 'vertical' }} />
        </Field>
      </div>

      <div style={cardStyle}>
        <SectionHeader>Mode</SectionHeader>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {([
            ['add', 'Add a record', 'Starts empty with the transaction’s defaults, as the Add form does.'],
            ['edit', 'Edit a record', 'Loads the record found below.'],
            ['add_or_edit', 'Edit it if found, else add one', 'Loads the record when found, starts a new one when not.'],
            ['fill', 'Fill in the open screen', 'Started from a button on the base transaction’s own screen (in a drawer): works on that screen’s record — saved or not — and hands back the values it set and the lines it added. It never saves; the user does.'],
          ] as const).map(([m, label, help]) => (
            <label key={m} style={{ display: 'flex', gap: 8, fontSize: 14, cursor: 'pointer' }}>
              <input type="radio" name="wz-mode" checked={model.mode === m} disabled={ctx.readOnly}
                onChange={() => patch({ mode: m })} />
              <span><span style={{ fontWeight: model.mode === m ? 600 : 400 }}>{label}</span>
                <div style={hintStyle}>{help}</div></span>
            </label>
          ))}
        </div>
        {editing && (
          <div style={{ marginTop: 10 }}>
            <Field label="Find the record where (header column → template; several matches → the user picks)">
              <KeyValueEditor value={model.locate?.match}
                onChange={(match) => patch({ locate: { ...model.locate, match } })}
                keyOptions={(header?.columns || []).map((c) => c.db_name || c.name)} keyPlaceholder="COLUMN"
                valuePlaceholder="{input.pick_no}" />
            </Field>
            <Field label="When none is found">
              <SelectInput value={model.locate?.on_missing?.startsWith('goto:') ? 'goto' : (model.locate?.on_missing || 'error')}
                onChange={(v) => patch({ locate: { match: model.locate?.match || {}, on_missing: v === 'goto' ? `goto:${model.steps[0]?.id || ''}` : v } })}
                options={[{ value: 'error', label: 'Stop with an error' }, { value: 'add', label: 'Start a new record' }, { value: 'goto', label: 'Go to a step' }]} />
              {model.locate?.on_missing?.startsWith('goto:') && (
                <StepPicker value={model.locate.on_missing.slice(5)}
                  onChange={(s) => patch({ locate: { match: model.locate?.match || {}, on_missing: `goto:${s}` } })} />
              )}
            </Field>
          </div>
        )}
      </div>

      <div style={cardStyle}>
        <SectionHeader>Server functions — code the Server function / Other website steps run</SectionHeader>
        <FunctionsEditor model={model} onChange={onChange} />
        <Check checked={model.unattended} label="Runs unattended (a follow-up, nobody watching)"
          hint="Check then refuses any step that needs a person. A transaction follow-up can start this wizard after a save or an action."
          onChange={(unattended) => patch({ unattended: unattended || undefined })} />
      </div>

      <div style={cardStyle}>
        <SectionHeader>Inputs — what every launch fills (menu, URL, action button, a calling wizard)</SectionHeader>
        <InputsEditor model={model} onChange={onChange} />
      </div>

      <div style={cardStyle}>
        <SectionHeader>Outputs — handed back to a calling wizard when this one finishes</SectionHeader>
        <KeyValueEditor value={model.outputs} onChange={(outputs) => patch({ outputs: outputs as Record<string, string> })}
          keyPlaceholder="output" valuePlaceholder="{run.txn_id}" />
      </div>

      <div style={cardStyle}>
        <SectionHeader>Variables</SectionHeader>
        <VarsEditor model={model} onChange={onChange} />
      </div>

      <div style={cardStyle}>
        <SectionHeader>Start, finish and pause</SectionHeader>
        <Grid2>
          <Field label="First step">
            <StepPicker value={model.start} onChange={(start) => patch({ start })} />
          </Field>
          <Field label="After the last step">
            <SelectInput value={finishMode}
              onChange={(v) => patch({ finish: { ...model.finish, then: v === 'goto' ? `goto:${model.steps[0]?.id || ''}` : v } })}
              options={[
                { value: 'restart', label: 'Start again (next record)' }, { value: 'close', label: 'Close the wizard' },
                { value: 'open_record', label: 'Open the saved record' }, { value: 'goto', label: 'Go to a step' },
              ]} />
            {finishMode === 'goto' && (
              <StepPicker value={finishThen.slice(5)} onChange={(s) => patch({ finish: { ...model.finish, then: `goto:${s}` } })} />
            )}
          </Field>
        </Grid2>
        <Field label="Finish message">
          <ExprInput mode="template" value={model.finish?.message}
            onChange={(message) => patch({ finish: { ...model.finish, message: message || undefined } })}
            placeholder="Pick {header.pick_no} saved" />
        </Field>
        <Grid2>
          <Field label="Pausing">
            <SelectInput value={model.suspend || 'memory'} onChange={(suspend) => patch({ suspend: suspend as 'memory' | 'server' })}
              options={[
                { value: 'memory', label: 'In this tab only' },
                { value: 'server', label: 'On the server — resumable from the Work List' },
              ]} />
          </Field>
          <Field label="Safe restart step (a model changed while paused)">
            <StepPicker value={model.safe_resume} empty="— first step —" onChange={(safe_resume) => patch({ safe_resume: safe_resume || undefined })} />
          </Field>
          <Field label="Max loop iterations">
            <NumberInput value={model.limits?.max_loop_iterations} min={1} placeholder="500"
              onChange={(n) => patch({ limits: { ...model.limits, max_loop_iterations: n } })} />
          </Field>
          <Field label="Max call depth">
            <NumberInput value={model.limits?.max_call_depth} min={1} placeholder="3"
              onChange={(n) => patch({ limits: { ...model.limits, max_call_depth: n } })} />
          </Field>
        </Grid2>
      </div>

      <div style={cardStyle}>
        <SectionHeader>Presentation</SectionHeader>
        <Grid2>
          <Field label="Density">
            <SelectInput value={model.presentation?.density || 'auto'}
              onChange={(density) => patch({ presentation: { ...model.presentation, density: density as 'auto' } })}
              options={[{ value: 'auto', label: 'By device' }, { value: 'handheld', label: 'Handheld' }, { value: 'desktop', label: 'Desktop' }]} />
          </Field>
          <Field label="Progress">
            <SelectInput value={model.presentation?.progress || 'path'}
              onChange={(progress) => patch({ presentation: { ...model.presentation, progress: progress as 'path' } })}
              options={[{ value: 'path', label: 'The path taken' }, { value: 'count', label: 'A step count' }]} />
          </Field>
        </Grid2>
        <Check checked={model.presentation?.show_record_panel !== false}
          onChange={(v) => patch({ presentation: { ...model.presentation, show_record_panel: v } })}
          label="Show the record so far beside the step (desktop)" />
        <Check checked={model.offline} onChange={(offline) => patch({ offline: offline || undefined })}
          label="Offline-capable (only offline-safe steps)" />
      </div>

      <div style={cardStyle}>
        <SectionHeader>What a save through this wizard may change</SectionHeader>
        {editing && (
          <Field label="Only records where (an expression over the STORED record)">
            <ExprInput value={model.scope?.edit_when}
              onChange={(edit_when) => patch({ scope: edit_when ? { edit_when } : undefined })}
              placeholder="STATUS in ('RELEASED', 'PICKING')" />
            {!model.scope?.edit_when && (
              <div style={{ ...hintStyle, color: 'var(--color-warning, #b45309)' }}>
                Without it, any record the user can see may be changed through this wizard.
              </div>
            )}
          </Field>
        )}
        <Field label="Also writes — header columns filled by business-logic / REST item changes or actions">
          <ColumnMulti form={header} prefix="header." values={alsoHeader}
            onChange={(h) => patch({ also_writes: [...h, ...alsoLine].length ? [...h, ...alsoLine] : undefined })} />
        </Field>
        <Field label="Also writes — line columns">
          <ColumnMulti form={lineForms(ctx.base).find((f) => f.form_no === (model.line_form || '2')) || lineForms(ctx.base)[0] || null}
            prefix="line." values={alsoLine}
            onChange={(l) => patch({ also_writes: [...alsoHeader, ...l].length ? [...alsoHeader, ...l] : undefined })} />
        </Field>
        <div style={hintStyle}>
          The server allows a save to change only what the wizard’s steps can set (plus these). Everything else
          must come back unchanged — see the writable set on the Check tab.
        </div>
      </div>
    </div>
  )
}
